import { TableCell } from '@mui/material';
import { styled } from '@mui/material/styles';
import {
    PatientListBodyCell,
    PatientListHeadCell,
    PatientTableRow,
} from '~/containers/patient-list/PatientList/PatientListTable';

/**
 * The column chassis the DICOM-source pane's two lists share with the worklist.
 *
 * The head cell, body cell and row are the worklist's own, imported rather than restyled: a result
 * list inside the import dialog and the list it imports into are the same kind of object, and two
 * near-identical row treatments would read as two conventions. Only the column sets differ, because
 * a 660px pane does not hold the worklist's 1340px track.
 */

export { PatientListBodyCell as PacsBodyCell };

/**
 * A one-line row, for a list read by scanning many entries rather than studying one: a lookup's
 * matches or a patient's series. The worklist's row is sized for a patient's whole summary, which
 * neither carries.
 */
export const PacsCompactRow = styled(PatientTableRow)(({ theme }) => ({
    height: 32,
    '& > td': {
        paddingTop: 0,
        paddingBottom: 0,
    },
    '& > td:first-of-type': {
        paddingLeft: theme.spacing(2),
    },
}));

/** Design track width in px; 'flex' absorbs the remaining table width. */
export type PacsTrackWidth = number | 'flex';

export type PacsColumn = {
    id: string;
    /** Absent draws no label, for the checkbox and chevron gutters. */
    label?: string;
    width: PacsTrackWidth;
    align?: 'right';
};

// Mirrors the worklist's cell padding so the two tables' content lands on the same tracks.
const CELL_PAD_X = 8;
const OUTER_PAD_LEFT = 16;
const OUTER_PAD_RIGHT = 24;

function colWidth(
    width: PacsTrackWidth,
    index: number,
    count: number
): number | undefined {
    if (width === 'flex') {
        return undefined;
    }
    const left = index === 0 ? OUTER_PAD_LEFT : CELL_PAD_X;
    const right = index === count - 1 ? OUTER_PAD_RIGHT : CELL_PAD_X;
    return width + left + right;
}

/** `<colgroup>` pinning the fixed tracks; `table-layout: fixed` makes them drive alignment. */
export function PacsColgroup({ columns }: { columns: readonly PacsColumn[] }) {
    return (
        <colgroup>
            {columns.map((column, index) => {
                const width = colWidth(column.width, index, columns.length);
                return (
                    <col
                        key={column.id}
                        style={width === undefined ? undefined : { width }}
                    />
                );
            })}
        </colgroup>
    );
}

/** Head cell carrying its column's alignment; the worklist's own treatment otherwise. */
export const PacsHeadCell = styled(PatientListHeadCell)({});

/**
 * A band spanning every column, for the study headings inside the series list. The only band there:
 * that list carries no head row, so nothing sits above this to pair with it into a two-line header.
 *
 * Its text is the body's ink rather than a head's off-ink, because a study date is a value the
 * source reported and the thing that tells two planning CTs of one patient apart.
 */
export const PacsGroupCell = styled(TableCell)(({ theme }) => ({
    position: 'sticky',
    top: 0,
    zIndex: 1,
    padding: theme.spacing(0, 1, 0, 2),
    height: 34,
    borderBottom: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.sunken,
    fontSize: 12,
    color: theme.palette.text.primary,
    whiteSpace: 'nowrap',
}));

/** Table props shared by both lists: fixed tracks, sticky head, no outer border. */
export const PACS_TABLE_SX = {
    tableLayout: 'fixed' as const,
    '& td, & th': { borderColor: 'divider' },
};
